// @flow
import React, { type Node } from 'react';
import { getWeekDay } from '@tectonics/prs/week-day';
import {
  getGatewayVoucherCode,
  getSafeDueDate,
  usePendingIncomingPayment,
} from '@tectonics/prs/incoming-payment';
import { PuntoXpressInformation } from './puntoxpress-information';
import { ShowBarcodePdfButton } from '../commons/show-barcode-pdf-button';
import { ButtonWrapper, VariableWidthDiv } from '../commons/render-helpers';
import { VoucherNumberBox } from '../commons/voucher-number-box';
import { VoucherText } from '../commons/voucher/voucher-text';
import { Separator } from '../../../atoms';
import { DisplayDueDate } from '../../../organisms/display-due-date';
import { DisplayTotalAmount } from '../../../organisms/display-total-amount';
import {
  DashedHr,
  PaymentInfoWrapper,
} from '../../../organisms/shared-styled-components';
import { RenderPaymentInfoContainer } from '../../../organisms/payment-info-display';
import { useScreenWidth } from '@tectonics/hooks';
import phoneTipSrc from './phone-tip.svg';
import { DisplayBarcode } from '@tectonics/look-and-feel/volcanes/pages/page-pending-payment/commons/display-barcode';
import { VoucherNumber, VoucherNumberWrapper } from '@tectonics/look-and-feel/uber/pages/pending-payment/payment-method/commons/voucher/number';

export function RenderTotalDueDateAndBarCode(): Node {
  const incomingPayment = usePendingIncomingPayment();
  return (
    <>
      <PaymentInfoWrapper>
        <DisplayTotalAmount label="Valor a pagar" location="left" redesign={true} />
        <DisplayDueDate label="Fecha de Vencimiento" sublabel={`Pagar hasta el ${getWeekDay(getSafeDueDate(incomingPayment), dueDateWeekDay)}`} redesign={true} />
        <VoucherNumberBox redesign={true}>
          <br/>
          <RenderVoucherText />
          <br />
          <DisplayBarcode countryCode="GT" />
          <DisplayBarcodeNumber />
          <br />
        </VoucherNumberBox>
      </PaymentInfoWrapper>
      <Separator direction="vertical" size={3} />
    </>
  );
}
export function RenderButtonArea(): Node {
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 840;
  if (isMobile) {
    return (
      <>
        <ButtonWrapper>
          <ShowBarcodePdfButton format="pdf" buttonMessage="Guardar boleta en pdf" />
        </ButtonWrapper>
      </>
    );
  }

  return (
    <>
      <ButtonWrapper>
        <ShowBarcodePdfButton buttonMessage="Imprimir Boleta" />
      </ButtonWrapper>
      <ButtonWrapper>
        <ShowBarcodePdfButton format="pdf" inverseStyle={true} buttonMessage="Guardar Boleta en PDF" />
      </ButtonWrapper>
    </>
  );
}

export function RenderPaymentInstructions(): Node {
  return (
    <>
      <DashedHr />
      <PuntoXpressInformation />
      <DashedHr />

      <RenderPaymentInfoContainer
        bgColor="#ffffff"
        h1Text="Instrucciones de pago"
        items={[
          {
            imgSrc: 'https://cdn.ebanx.com/images/vouchers/cobro-icon.svg',
            subText: 'Después de realizar tu pago, verifica que en el comprobante emitido por el cajero esté escrito “Cobro de EBANX”.',
          },
          {
            imgSrc: phoneTipSrc,
            subText: 'La confirmación puede tardar hasta 10 minutos en realizarse.',
          },
        ]}
      />

      <DashedHr />

      <RenderPaymentInfoContainer
        h1Text="¿Ya conoces la Central de Ayuda EBANX?"
        items={[
          {
            imgSrc: 'https://cdn.ebanx.com/images/vouchers/files-icon.svg',
            subText: 'Si tienes dudas adicionales acerca de tu pago, o tu reembolso visítanos en la Central de Ayuda EBANX:',
            strong: 'ayuda.ebanx.com/hc/es-mx',
          },
        ]}
      />
    </>
  );
}

export function RenderVoucherText(): Node {
  return (
    <>
      <VoucherText><b>Datos para pago</b></VoucherText>
      <br />
      <VariableWidthDiv width="90">Dicta la referencia o escanea el código de barras para pagar.</VariableWidthDiv>
    </>
  );
}

export function DisplayBarcodeNumber(): Node {
  const incomingPayment = usePendingIncomingPayment();
  let voucherCode = getGatewayVoucherCode(incomingPayment);
  if (!voucherCode) {
    return null;
  }

  const voucherCodeLength = voucherCode.length;
  const piecesLength = 3;
  let voucherEnd = '';

  const shouldSplitPieces = voucherCodeLength % 3;
  if (shouldSplitPieces !== 0) {
    const splitPieces = shouldSplitPieces === 1 ? 4 : 2;
    voucherEnd = voucherCode.slice(-splitPieces);
    voucherCode = voucherCode.replace(voucherEnd, '');
  }

  const voucherSplitRegex = new RegExp(`(.{1,${piecesLength}})`, 'g');
  const voucherCodeSplitted = voucherCode.match(voucherSplitRegex) || [];

  if (voucherEnd.length) {
    voucherCodeSplitted.push(voucherEnd);
  }

  return (
    <VoucherNumberWrapper redesign={true}>
      {voucherCodeSplitted.map((code, index) => (
        <VoucherNumber key={index} redesign={true}>{code}</VoucherNumber>
      ))}
    </VoucherNumberWrapper>
  );
}

const dueDateWeekDay = [
  'domingo',
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
];
